/**
 * モーダル（data 属性駆動）
 *
 * マークアップ規約:
 *   <button data-lms-modal-open="pw-change">パスワード変更</button>
 *
 *   <div class="lms-overlay" id="pw-change" data-lms-modal hidden>
 *     <div class="lms-modal" role="dialog" aria-modal="true" aria-labelledby="pw-change-title">
 *       <button data-lms-modal-close>×</button>
 *       …
 *     </div>
 *   </div>
 *
 * 任意属性:
 *   data-lms-modal-static  ← ESC / オーバーレイクリックで閉じない（初回パスワード設定モーダル用）
 *
 * 挙動:
 *   - 開いている間は body のスクロールを止める（1 枚でも開いていれば固定し、全部閉じたら解除する）
 *   - 開いたら最初のフォーカス可能要素へフォーカスする
 *   - 閉じたら「そのモーダルを開いた時点でフォーカスされていた要素」へ戻す。
 *     モーダルは重ねて開けるため、焦点の記録もスタックで持ち 1 枚ごとに対応する起点へ戻す。
 *     入れ子（2 枚以上）の上段だけを閉じたときは、下段のモーダル内へフォーカスが戻る
 *     （fix-nested-modal-focus D1。以前は最初の 1 枚分しか記録しておらず、上段を閉じると
 *      フォーカスが body に落ちて背景へ抜けていた）
 *   - 戻す先が DOM から外れている / 非表示 / 残っているモーダルの外にある場合は、
 *     最前面モーダルの最初のフォーカス可能要素へ戻す。開いているモーダルが 1 枚も残って
 *     いなければフォーカスは動かさない（fix-nested-modal-focus 05_ui.md §5 の決定表）
 *   - Tab / Shift+Tab をモーダル内に閉じ込める。フォーカスがモーダルの外にある場合は
 *     どちらの方向でも最前面モーダル内へ引き戻す（同 D2）
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * 開いているモーダル 1 枚分の状態。
 * モーダル要素と「そのモーダルを開いた時点のフォーカス」を必ず対で持つことで、
 * 2 本の配列を並走させたときに起きる同期ずれを型レベルで防ぐ（design.md D1 案 B）。
 */
interface OpenModalEntry {
  /** 開いているオーバーレイ要素（[data-lms-modal]） */
  readonly modal: HTMLElement;
  /** このモーダルを開いた時点でフォーカスされていた要素。閉じたときの復帰先の第 1 候補 */
  readonly origin: HTMLElement | null;
}

/** 現在開いているモーダルのスタック（最前面が末尾） */
const openStack: OpenModalEntry[] = [];

function focusables(modal: HTMLElement): HTMLElement[] {
  return Array.from(modal.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
}

/** openStack 内の位置。見つからなければ -1 */
function indexOfModal(modal: HTMLElement): number {
  return openStack.findIndex((entry) => entry.modal === modal);
}

function topModal(): HTMLElement | undefined {
  return openStack[openStack.length - 1]?.modal;
}

/**
 * フォーカスの復帰先として使える要素か。
 * 可視判定は focusables() と同じ基準（offsetParent）に揃える。判定基準を 2 種類作らない。
 */
function isRestorable(el: HTMLElement | null): el is HTMLElement {
  return el !== null && el.isConnected && el.offsetParent !== null;
}

/** モーダル内の最初のフォーカス可能要素（無ければモーダル自身）へフォーカスする */
function focusInside(modal: HTMLElement): void {
  (focusables(modal)[0] ?? modal).focus?.();
}

/**
 * モーダルを 1 枚閉じたあとのフォーカス復帰（05_ui.md §5 の決定表）。
 *   - 開いたままのモーダルが残っている場合、復帰先は必ずその最前面モーダルの内側にする。
 *     起点が使えない、あるいは最前面モーダルの外を指していたら先頭のフォーカス可能要素へ。
 *     これにより「フォーカスがモーダルの外へ出ない」ことを経路によらず構造的に保証する。
 *   - すべて閉じた場合は起点へ戻す。起点が使えなければ何もしない（背景の任意要素を勝手に
 *     選ぶとスクリーンリーダーの読み上げ位置が予測不能に飛ぶため）。
 */
function restoreFocus(origin: HTMLElement | null): void {
  const top = topModal();

  if (top) {
    if (isRestorable(origin) && top.contains(origin)) {
      origin.focus?.();
    } else {
      focusInside(top);
    }
    return;
  }

  if (isRestorable(origin)) origin.focus?.();
}

export function openModal(modal: HTMLElement): void {
  if (indexOfModal(modal) !== -1) return;

  if (openStack.length === 0) {
    document.body.style.overflow = "hidden";
  }

  // 焦点起点は「最初の 1 枚」ではなく開くたびに毎回記録する。
  // 入れ子の上段を閉じたときに下段のモーダル内へ戻すために必要（design.md D1）。
  const origin = document.activeElement instanceof HTMLElement ? document.activeElement : null;

  modal.hidden = false;
  openStack.push({ modal, origin });

  focusInside(modal);

  // 開いたタイミングでの遅延取得（user-admin ui/remote-list.ts 等）が使うフック
  modal.dispatchEvent(new CustomEvent("lms:modal:open", { bubbles: true }));
}

/**
 * `closeModal` / `closeAllModals` の共通本体。
 *
 * `restore` で焦点復帰の実行を制御する。`closeAllModals` が 1 枚ずつここを呼ぶ際は
 * `restore: false` を渡し、ループの中間状態への `.focus()` を発生させない
 * （実装時の訂正・2026-07-28: fix-nested-modal-focus レビュー Medium-2。
 * 中間状態は次のクローズで即座に上書きされるだけであり、実ブラウザでは
 * focus/blur イベントが同期発火するため、フォーカスタイミングに依存する他の
 * 部品（`dropdown.ts` 等）が中間状態で意図せず反応しうる。最終的な復帰先への
 * 1 回だけに集約するのが正しい）。
 */
function closeModalCore(modal: HTMLElement, restore: boolean): void {
  const idx = indexOfModal(modal);
  if (idx === -1) return;

  const entry = openStack[idx]!;
  const wasTop = idx === openStack.length - 1;

  // hidden にするとブラウザは非表示になった要素からフォーカスを外す（body へ移す）ため、
  // 「閉じる直前にフォーカスがこのモーダル内にあったか」は必ず hidden より前に読む。
  const activeBefore = document.activeElement;
  const hadFocusInside = activeBefore instanceof HTMLElement && modal.contains(activeBefore);

  modal.hidden = true;
  openStack.splice(idx, 1);

  // スクロール固定の解除条件は変更しない（全閉時のみ。design.md D1）
  if (openStack.length === 0) {
    document.body.style.overflow = "";
  }

  // 最前面を閉じたときは常に復帰させる。最前面でないモーダルを閉じた場合は、
  // そのモーダルがフォーカスを持っていたときだけ動かす（最前面が持っているなら触らない）。
  if (restore && (wasTop || hadFocusInside)) {
    restoreFocus(entry.origin);
  }
}

export function closeModal(modal: HTMLElement): void {
  closeModalCore(modal, true);
}

/**
 * 開いているモーダルをすべて閉じる（部分更新でコンテンツ領域が差し替わる直前に呼ぶ）。
 *
 * 焦点復帰は最後に 1 回だけ行う。ループの各ステップで `closeModalCore(modal, true)` を
 * 呼ぶと、N 枚のスタックを一括で閉じたときに中間状態への `.focus()` が N 回発生してしまう
 * （実装時の訂正・2026-07-28: fix-nested-modal-focus レビュー Medium-2）。
 * 最も外側（最初に開いた）モーダルの起点だけを覚えておき、全部閉じ終えたあとに
 * 決定表 #4/#5 と同じ規則（生存・可視なら復帰、それ以外は何もしない）を 1 回だけ適用する。
 */
export function closeAllModals(): void {
  if (openStack.length === 0) return;

  const outermostOrigin = openStack[0]!.origin;

  while (openStack.length > 0) {
    closeModalCore(openStack[openStack.length - 1]!.modal, false);
  }

  if (isRestorable(outermostOrigin)) outermostOrigin.focus?.();
}

function isStatic(modal: HTMLElement): boolean {
  return modal.hasAttribute("data-lms-modal-static");
}

export function initModal(scope: ParentNode = document): void {
  // 開くトリガー
  scope.querySelectorAll<HTMLElement>("[data-lms-modal-open]").forEach((trigger) => {
    if (trigger.dataset["lmsModalReady"] === "1") return;
    trigger.dataset["lmsModalReady"] = "1";

    trigger.addEventListener("click", (e) => {
      const id = trigger.dataset["lmsModalOpen"];
      if (!id) return;
      const modal = document.getElementById(id);
      if (!modal) return;
      e.preventDefault();
      openModal(modal);
    });
  });

  // 閉じるトリガー / オーバーレイクリック
  scope.querySelectorAll<HTMLElement>("[data-lms-modal]").forEach((modal) => {
    if (modal.dataset["lmsModalReady"] === "1") return;
    modal.dataset["lmsModalReady"] = "1";
    modal.hidden = true;

    modal.querySelectorAll<HTMLElement>("[data-lms-modal-close]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        closeModal(modal);
      });
    });

    modal.addEventListener("mousedown", (e) => {
      // オーバーレイ自身が押されたときのみ閉じる（内側のドラッグ終了で閉じないように mousedown で判定）
      if (e.target === modal && !isStatic(modal)) {
        closeModal(modal);
      }
    });
  });

  // 初期表示で開くモーダル（サーバー側バリデーションエラーからの復帰。user-admin 05_ui.md §4.4）
  scope.querySelectorAll<HTMLElement>("[data-lms-open-on-load]").forEach((m) => openModal(m));
}

/**
 * data-lms-modal-autoopen が付いたモーダルを初期化直後に開く。
 * サーバー側バリデーションエラーの復元（初回パスワード設定・パスワード変更）に使う。
 * 複数ある場合は DOM 順で最後のものだけを開く（05_ui.md §7.2）。
 */
export function initModalAutoOpen(scope: ParentNode = document): void {
  const targets = scope.querySelectorAll<HTMLElement>("[data-lms-modal][data-lms-modal-autoopen]");
  const last = targets[targets.length - 1];
  if (last) openModal(last);
}

/** ESC キーとフォーカストラップ。アプリ起動時に 1 度だけ呼ぶ */
export function initModalKeyboard(): void {
  document.addEventListener("keydown", (e) => {
    const modal = topModal();
    if (!modal) return;

    if (e.key === "Escape" && !isStatic(modal)) {
      e.preventDefault();
      closeModal(modal);
      return;
    }

    if (e.key !== "Tab") return;

    const items = focusables(modal);
    if (items.length === 0) {
      e.preventDefault();
      return;
    }
    const first = items[0]!;
    const last = items[items.length - 1]!;
    const active = document.activeElement;

    // フォーカスがモーダルの外にある場合は、Tab / Shift+Tab のどちらでも引き戻す
    // （design.md D2。前方分岐にだけ外側ガードが無く、外へ出た焦点を素通りさせていた）
    if (e.shiftKey && (active === first || !modal.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !modal.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  });
}
